import { sortBy } from 'ramda'

import {
  LearnerSnapshot,
  LearnerSnapshotWeek,
  LicenseGroupBreakdown,
} from 'src/v3/content-pipeline/performance.slice'

export type LicenseKey = keyof Omit<LicenseGroupBreakdown, 'total'>

export let LICENSE_KEYS: LicenseKey[] = [
  'paramedic',
  'emt',
  'aemt',
  'nurse',
  'other',
]

export let LICENSE_LABELS: Record<LicenseKey, string> = {
  paramedic: 'Paramedic',
  emt: 'EMT',
  aemt: 'AEMT',
  nurse: 'Nurse',
  other: 'Other',
}

export let LICENSE_COLORS: Record<LicenseKey, string> = {
  paramedic: '#1976d2',
  emt: '#ab47bc',
  aemt: '#66bb6a',
  nurse: '#26a69a',
  other: '#ffa726',
}

export type DerivedKPIs = {
  totalEnrolled: number
  completionRatePct: number
  firstAttemptPassRatePct: number
  activeLast30Days: number
  enrolledDelta: number | null
  completionRateDelta: number | null
  firstAttemptDelta: number | null
  activeDelta: number | null
  enrolledSparkline: number[]
  completionRateSparkline: number[]
  firstAttemptSparkline: number[]
  weeksCovered: number
}

export type LicenseBreakdownRow = {
  label: string
  key: LicenseKey
  count: number
}

export let sliceSeries = (
  series: LearnerSnapshotWeek[],
  months: number | null,
): LearnerSnapshotWeek[] => {
  if (months === null) return series
  let cutoff = new Date()
  cutoff.setMonth(cutoff.getMonth() - months)
  let cutoffStr = cutoff.toISOString().slice(0, 10)
  return series.filter((w) => w.weekStart >= cutoffStr)
}

let sumAcrossLicenses = (
  week: LearnerSnapshotWeek,
  field: 'enrollments' | 'completions',
  licenses: LicenseKey[],
): number => licenses.reduce((sum, key) => sum + (week[field][key] || 0), 0)

let sumField = (
  weeks: LearnerSnapshotWeek[],
  field: 'enrollments' | 'completions',
  licenses: LicenseKey[],
): number =>
  weeks.reduce((sum, w) => sum + sumAcrossLicenses(w, field, licenses), 0)

let safePct = (numerator: number, denominator: number): number =>
  denominator > 0 ? Math.round((numerator / denominator) * 1000) / 10 : 0

let computeDelta = (current: number, previous: number): number | null => {
  if (previous === 0) return null
  return Math.round(((current - previous) / previous) * 1000) / 10
}

let computeDeltaPoints = (current: number, previous: number): number | null => {
  if (previous === 0 && current === 0) return null
  return Math.round((current - previous) * 10) / 10
}

export let resolveLicenses = (selected: Set<LicenseKey>): LicenseKey[] =>
  selected.size === 0
    ? LICENSE_KEYS
    : LICENSE_KEYS.filter((k) => selected.has(k))

export let deriveKPIs = (
  snapshot: LearnerSnapshot,
  months: number | null,
  licenses: LicenseKey[],
): DerivedKPIs => {
  let sliced = sliceSeries(snapshot.series, months)
  let halfLen = Math.ceil(sliced.length / 2)
  let currentHalf = sliced.slice(halfLen)
  let previousHalf = sliced.slice(0, halfLen)

  let totalEnrolled = sumField(sliced, 'enrollments', licenses)
  let totalCompleted = sumField(sliced, 'completions', licenses)
  let completionRatePct = safePct(totalCompleted, totalEnrolled)

  let totalQuizAttempts = sliced.reduce((s, w) => s + w.quizAttempts, 0)
  let totalFirstAttemptPasses = sliced.reduce(
    (s, w) => s + w.firstAttemptPasses,
    0,
  )
  let firstAttemptPassRatePct = safePct(
    totalFirstAttemptPasses,
    totalQuizAttempts,
  )

  let prevEnrolled = sumField(previousHalf, 'enrollments', licenses)
  let prevCompleted = sumField(previousHalf, 'completions', licenses)
  let prevCompletionRate = safePct(prevCompleted, prevEnrolled)
  let prevQuizAttempts = previousHalf.reduce((s, w) => s + w.quizAttempts, 0)
  let prevFirstPasses = previousHalf.reduce(
    (s, w) => s + w.firstAttemptPasses,
    0,
  )
  let prevFirstAttemptRate = safePct(prevFirstPasses, prevQuizAttempts)

  let curEnrolled = sumField(currentHalf, 'enrollments', licenses)

  let enrolledSparkline = sliced.map((w) =>
    sumAcrossLicenses(w, 'enrollments', licenses),
  )

  let completionRateSparkline = sliced.map((w) => {
    let enrolled = sumAcrossLicenses(w, 'enrollments', licenses)
    let completed = sumAcrossLicenses(w, 'completions', licenses)
    return safePct(completed, enrolled)
  })

  let firstAttemptSparkline = sliced.map((w) =>
    safePct(w.firstAttemptPasses, w.quizAttempts),
  )

  return {
    totalEnrolled,
    completionRatePct,
    firstAttemptPassRatePct,
    activeLast30Days: snapshot.totals.activeLast30Days,
    enrolledDelta: computeDelta(curEnrolled, prevEnrolled),
    completionRateDelta: computeDeltaPoints(
      completionRatePct,
      prevCompletionRate,
    ),
    firstAttemptDelta: computeDeltaPoints(
      firstAttemptPassRatePct,
      prevFirstAttemptRate,
    ),
    activeDelta: null,
    enrolledSparkline,
    completionRateSparkline,
    firstAttemptSparkline,
    weeksCovered: sliced.length,
  }
}

export let deriveLicenseBreakdown = (
  series: LearnerSnapshotWeek[],
  months: number | null,
): LicenseBreakdownRow[] => {
  let sliced = sliceSeries(series, months)
  let rows = LICENSE_KEYS.map((key) => ({
    label: LICENSE_LABELS[key],
    key,
    count: sliced.reduce((sum, w) => sum + (w.completions[key] || 0), 0),
  }))
  return sortBy((r) => -r.count, rows)
}

export type CategoryStat = {
  key: LicenseKey
  label: string
  enrolled: number
  completed: number
  completionRatePct: number
  enrolledDelta: number | null
  completionRateDelta: number | null
  sharePct: number
}

export type CategoryTrendPoint = {
  weekStart: string
  date: Date
  paramedic: number
  emt: number
  aemt: number
  nurse: number
  other: number
}

export type TrendPoint = {
  weekStart: string
  date: Date
  enrolled: number
  completed: number
}

export let deriveTrendSeries = (
  series: LearnerSnapshotWeek[],
  months: number | null,
  licenses: LicenseKey[],
): TrendPoint[] => {
  let sliced = sliceSeries(series, months)
  return sliced.map((w) => ({
    weekStart: w.weekStart,
    date: new Date(w.weekStart),
    enrolled: sumAcrossLicenses(w, 'enrollments', licenses),
    completed: sumAcrossLicenses(w, 'completions', licenses),
  }))
}

export let deriveCategoryTrendSeries = (
  series: LearnerSnapshotWeek[],
  months: number | null,
  metric: 'enrollments' | 'completions' = 'completions',
): CategoryTrendPoint[] => {
  let sliced = sliceSeries(series, months)
  return sliced.map((w) => ({
    weekStart: w.weekStart,
    date: new Date(w.weekStart),
    paramedic: w[metric].paramedic || 0,
    emt: w[metric].emt || 0,
    aemt: w[metric].aemt || 0,
    nurse: w[metric].nurse || 0,
    other: w[metric].other || 0,
  }))
}

export let toShareSeries = (
  points: CategoryTrendPoint[],
): CategoryTrendPoint[] =>
  points.map((p) => {
    let total = LICENSE_KEYS.reduce((s, k) => s + (p[k] || 0), 0)
    if (total === 0) return p
    return {
      weekStart: p.weekStart,
      date: p.date,
      paramedic: Math.round((p.paramedic / total) * 1000) / 10,
      emt: Math.round((p.emt / total) * 1000) / 10,
      aemt: Math.round((p.aemt / total) * 1000) / 10,
      nurse: Math.round((p.nurse / total) * 1000) / 10,
      other: Math.round((p.other / total) * 1000) / 10,
    }
  })

export let smoothSeries = <T extends Record<string, unknown>>(
  points: T[],
  keys: (keyof T)[],
  window: number,
): T[] => {
  if (window <= 1 || points.length === 0) return points
  return points.map((p, i) => {
    let start = Math.max(0, i - Math.floor(window / 2))
    let end = Math.min(points.length, start + window)
    let slice = points.slice(start, end)
    let averaged = keys.reduce<Record<string, unknown>>((acc, key) => {
      let avg =
        slice.reduce((s, v) => s + ((v[key] as number) || 0), 0) / slice.length
      return { ...acc, [key as string]: Math.round(avg * 10) / 10 }
    }, {})
    return { ...p, ...averaged } as T
  })
}

export let deriveCategoryStats = (
  series: LearnerSnapshotWeek[],
  months: number | null,
): CategoryStat[] => {
  let sliced = sliceSeries(series, months)
  let halfLen = Math.ceil(sliced.length / 2)
  let currentHalf = sliced.slice(halfLen)
  let previousHalf = sliced.slice(0, halfLen)

  let totalCompletedAll = LICENSE_KEYS.reduce(
    (sum, key) =>
      sum + sliced.reduce((s, w) => s + (w.completions[key] || 0), 0),
    0,
  )

  return LICENSE_KEYS.map((key) => {
    let enrolled = sliced.reduce((s, w) => s + (w.enrollments[key] || 0), 0)
    let completed = sliced.reduce((s, w) => s + (w.completions[key] || 0), 0)
    let completionRatePct = safePct(completed, enrolled)

    let curEnrolled = currentHalf.reduce(
      (s, w) => s + (w.enrollments[key] || 0),
      0,
    )
    let prevEnrolled = previousHalf.reduce(
      (s, w) => s + (w.enrollments[key] || 0),
      0,
    )
    let curCompleted = currentHalf.reduce(
      (s, w) => s + (w.completions[key] || 0),
      0,
    )
    let prevCompleted = previousHalf.reduce(
      (s, w) => s + (w.completions[key] || 0),
      0,
    )
    let curRate = safePct(curCompleted, curEnrolled)
    let prevRate = safePct(prevCompleted, prevEnrolled)

    return {
      key,
      label: LICENSE_LABELS[key],
      enrolled,
      completed,
      completionRatePct,
      sharePct: safePct(completed, totalCompletedAll),
      enrolledDelta: computeDelta(curEnrolled, prevEnrolled),
      completionRateDelta: computeDeltaPoints(curRate, prevRate),
    }
  })
}
